import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Device } from '@capacitor/device';
import { ActionPerformed, PushNotifications, PushNotificationSchema, Token } from '@capacitor/push-notifications';
import { Channel } from '@capacitor/push-notifications/dist/esm/definitions';
import { Session } from '@core/models/Session';
import { SessionService } from '@core/services/session.service';
import { Platform, ToastController } from '@ionic/angular';
import { Store } from '@ngxs/store';
import { lastValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ChatState } from '../../home/chat/store/chat.state';
const NOTIFICATION_CHANNELS: Channel[] = [
  { id: 'chat_channel', name: 'Chat Notifications', description: 'Notificaciones para mensajes de chat', importance: 4 },
  { id: 'routes_channel', name: 'Route Updates', description: 'Notificaciones relacionadas con rutas', importance: 4 },
  { id: 'forms_channel', name: 'Form Updates', description: 'Notificaciones relacionadas con formularios', importance: 4 },
];
@Injectable({
  providedIn: 'root',
})
export class PushNotificationService {
  deviceId: string | null = null;
  platform: 'ios' | 'android' | 'web' = null;
  session: Session;
  chatIsOpen = false;
  constructor(private http: HttpClient, private router: Router, private _platform: Platform, private toast: ToastController, private _store: Store, private sessionService: SessionService) {
    this._store.select(ChatState.isOpen).subscribe(isOpen => this.chatIsOpen = isOpen);
    this.sessionService.logout$.subscribe(() => this.unregisterPushNotifications());
  }
  async initializePushNotifications(session: Session) {
    this.session = session;
    if (!this.session) {
      return;
    }
    if (this._platform.is('ios') || this._platform.is('android')) {
      try {
        await this.registerPushNotifications();
        await this.createNotificationChannels();
        await this.getDeviceId();
        this.initializeListeners();
      } catch (e) {
        console.log('Error al inicializar las notificaciones push: ', e);
      }
    }
  }
  async unregisterPushNotifications() {
    if (!this.session?.driver?.phone || !this.deviceId || !this.platform) {
      return;
    }
    try {
      await lastValueFrom(
        this.http.post(`${environment.apiNotifications.url}/devices/unregister`, {
          phoneNumber: this.session.driver.phone,
          deviceId: this.deviceId,
          platform: this.platform,
        }),
      );
      await PushNotifications.removeAllListeners();
    } catch (error: any) {
      console.error('Error al desregistrar el token en el backend:', error);
    }
  }
  async getDeviceId() {
    const info = await Device.getId();
    const deviceInfo = await Device.getInfo();
    this.platform = deviceInfo.platform;
    this.deviceId = info.identifier;
  }
  async createNotificationChannels() {
    if (this._platform.is('android')) {
      for (const channel of NOTIFICATION_CHANNELS) {
        await PushNotifications.createChannel(channel);
      }
    }
  }
  async handleNotificationAction(action: ActionPerformed) {
    const data = action.notification.data || {};
    if (data.click_action) {
      await this.router.navigate([data.click_action]);
    } else if (data.url) {
      window.open(data.url, '_blank');
    } else {
      console.log('Acción desconocida en la notificación:', data);
    }
  }
  private async registerPushNotifications() {
    let permissionStatus = await PushNotifications.checkPermissions();
    if (permissionStatus.receive == 'prompt') {
      permissionStatus = await PushNotifications.requestPermissions();
    }
    if (permissionStatus.receive != 'granted') {
      alert('El usuario denegó los permisos de notificaciones');
    }
    if (permissionStatus.receive == 'granted') {
      try {
        await PushNotifications.register();
      } catch (e: any) {
        console.log('error en el registerPush', JSON.stringify(e));
        alert(JSON.stringify(e));
      }
    }
  }
  private initializeListeners() {
    if (!this.session?.driver?.phone) {
      return;
    }
    PushNotifications.addListener('registration', async (token: Token) => {
      const deviceData = {
        phoneNumber: this.session.driver.phone,
        deviceId: this.deviceId,
        deviceToken: token.value,
        platform: this.platform,
      };
      try {
        await lastValueFrom(this.http.post(`${environment.apiNotifications.url}/devices/register`, deviceData));
        console.log('Token registrado en el backend:');
      } catch (error: any) {
        console.error('ABC1235, Error al registrar el token en el backend:', error);
      }
    });
    PushNotifications.addListener('pushNotificationActionPerformed', (action: ActionPerformed) => this.handleNotificationAction(action));
    PushNotifications.addListener('pushNotificationReceived', async (notification: PushNotificationSchema) => {
      const data = notification.data || {};
      const toast = await this.toast.create({
        animated: true,
        message: `${notification.title}`,
        duration: 5000,
        position: 'bottom',
        color: 'primary',
        buttons: [
          {
            text: 'Ver',
            handler: async () => {
              if (data.click_action) {
                await this.router.navigate([data.click_action]);
              } else if (data.url) {
                window.open(data.url, '_blank');
              } else {
                console.log('Acción desconocida en la notificación:', data);
              }
            },
          },
        ],
      });
      if (data?.channel == 'chat_channel' && !this.chatIsOpen) {
        await toast.present();
      } else if (data?.channel == 'routes_channel') {
        await toast.present();
      } else if (data?.channel == 'forms_channel') {
        await toast.present();
      }
    });
  };
}
